import { OrbitControls } from '@react-three/drei'
import { useThree, type Object3DNode, Canvas, extend } from '@react-three/fiber'
import { useCallback, useEffect, useRef, useState } from 'react'
import { Color, Scene, Fog, PerspectiveCamera, Vector3 } from 'three'
import ThreeGlobe from 'three-globe'
import countries from '#app/data/globe.json'

declare module '@react-three/fiber' {
	interface ThreeElements {
		threeGlobe: Object3DNode<ThreeGlobe, typeof ThreeGlobe>
	}
}

extend({ ThreeGlobe })

const RING_PROPAGATION_SPEED = 3
const aspect = 1.2
const cameraZ = 300
const JAPAN_POSITION = { lat: 36.2048, lng: 230.6503 }
const NETHERLANDS_POSITION = { lat: 52.1326, lng: 5.2913 }
const ANIMATION_DURATION = 5000

type Position = {
	order: number
	startLat: number
	startLng: number
	endLat: number
	endLng: number
	arcAlt: number
	color: string
}

export type GlobeConfig = {
	pointSize?: number
	globeColor?: string
	showAtmosphere?: boolean
	atmosphereColor?: string
	atmosphereAltitude?: number
	emissive?: string
	emissiveIntensity?: number
	shininess?: number
	polygonColor?: string
	ambientLight?: string
	directionalLeftLight?: string
	directionalTopLight?: string
	pointLight?: string
	arcTime?: number
	arcLength?: number
	rings?: number
	maxRings?: number
	initialPosition?: {
		lat: number
		lng: number
	}
	autoRotate?: boolean
	autoRotateSpeed?: number
}

interface WorldProps {
	globeConfig: GlobeConfig
	data: Position[]
}

let numbersOfRings = [0]

export function Globe({ globeConfig, data }: WorldProps) {
	const [globeData, setGlobeData] = useState<
		| {
				size: number
				order: number
				color: (t: number) => string
				lat: number
				lng: number
		  }[]
		| null
	>(null)

	const globeRef = useRef<ThreeGlobe | null>(null)

	const defaultProps = {
		pointSize: 1,
		atmosphereColor: '#ffffff',
		showAtmosphere: true,
		atmosphereAltitude: 0.1,
		polygonColor: 'rgba(255,255,255,0.7)',
		globeColor: '#1d072e',
		emissive: '#000000',
		emissiveIntensity: 0.1,
		shininess: 0.9,
		arcTime: 2000,
		arcLength: 0.9,
		rings: 1,
		maxRings: 3,
		...globeConfig,
	}

	useEffect(() => {
		if (globeRef.current) {
			_buildData()
			_buildMaterial()
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [globeRef.current])

	const _buildMaterial = () => {
		if (!globeRef.current) return

		const globeMaterial = globeRef.current.globeMaterial() as unknown as {
			color: Color
			emissive: Color
			emissiveIntensity: number
			shininess: number
		}
		globeMaterial.color = new Color(globeConfig.globeColor)
		globeMaterial.emissive = new Color(globeConfig.emissive)
		globeMaterial.emissiveIntensity = globeConfig.emissiveIntensity || 0.1
		globeMaterial.shininess = globeConfig.shininess || 0.9
	}

	const _buildData = () => {
		const arcs = data
		let points = []
		for (const element of arcs) {
			const arc = element
			const rgb = hexToRgb(arc.color) as { r: number; g: number; b: number }
			points.push({
				size: defaultProps.pointSize,
				order: arc.order,
				color: (t: number) => `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${1 - t})`,
				lat: arc.startLat,
				lng: arc.startLng,
			})
			points.push({
				size: defaultProps.pointSize,
				order: arc.order,
				color: (t: number) => `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${1 - t})`,
				lat: arc.endLat,
				lng: arc.endLng,
			})
		}

		// remove duplicates for same lat and lng
		const filteredPoints = points.filter(
			(v, i, a) =>
				a.findIndex((v2) =>
					['lat', 'lng'].every(
						(k) => v2[k as 'lat' | 'lng'] === v[k as 'lat' | 'lng'],
					),
				) === i,
		)

		setGlobeData(filteredPoints)
	}

	const startAnimation = useCallback(() => {
		if (!globeRef.current || !globeData) return

		globeRef.current
			.arcsData(data)
			.arcStartLat((d) => (d as { startLat: number }).startLat * 1)
			.arcStartLng((d) => (d as { startLng: number }).startLng * 1)
			.arcEndLat((d) => (d as { endLat: number }).endLat * 1)
			.arcEndLng((d) => (d as { endLng: number }).endLng * 1)
			.arcColor((e: any) => (e as { color: string }).color)
			.arcAltitude((e) => {
				return (e as { arcAlt: number }).arcAlt * 1
			})
			.arcStroke((_e: object): number => {
				return [0.32, 0.28, 0.3][Math.round(Math.random() * 2)] || 0.3
			})
			.arcDashLength(defaultProps.arcLength)
			.arcDashInitialGap((e) => (e as { order: number }).order * 1)
			.arcDashGap(15)
			.arcDashAnimateTime(() => defaultProps.arcTime)

		globeRef.current
			.pointsData(data)
			.pointColor((e) => (e as { color: string }).color)
			.pointsMerge(true)
			.pointAltitude(0.0)
			.pointRadius(2)

		globeRef.current
			.ringsData([])
			.ringColor((e: any) => (t: any) => e.color(t))
			.ringMaxRadius(defaultProps.maxRings)
			.ringPropagationSpeed(RING_PROPAGATION_SPEED)
			.ringRepeatPeriod(
				(defaultProps.arcTime * defaultProps.arcLength) / defaultProps.rings,
			)
	}, [
		globeData,
		data,
		defaultProps.arcLength,
		defaultProps.maxRings,
		defaultProps.arcTime,
		defaultProps.rings,
	])

	useEffect(() => {
		if (globeRef.current && globeData) {
			globeRef.current
				.hexPolygonsData(countries.features)
				.hexPolygonResolution(3)
				.hexPolygonMargin(0.7)
				.showAtmosphere(defaultProps.showAtmosphere)
				.atmosphereColor(defaultProps.atmosphereColor)
				.atmosphereAltitude(defaultProps.atmosphereAltitude)
				.hexPolygonColor(() => {
					return defaultProps.polygonColor
				})
			startAnimation()
		}
	}, [
		defaultProps.atmosphereAltitude,
		defaultProps.atmosphereColor,
		defaultProps.polygonColor,
		defaultProps.showAtmosphere,
		globeData,
		startAnimation,
	])

	useEffect(() => {
		if (!globeRef.current || !globeData) return

		const interval = setInterval(() => {
			if (!globeRef.current || !globeData) return
			numbersOfRings = genRandomNumbers(
				0,
				data.length,
				Math.floor((data.length * 4) / 5),
			)

			globeRef.current.ringsData(
				globeData.filter((d, i) => numbersOfRings.includes(i)),
			)
		}, 2000)

		return () => {
			clearInterval(interval)
		}
	}, [globeData, data.length])

	return <threeGlobe ref={globeRef} />
}

export function WebGLRendererConfig() {
	const { gl, size } = useThree()

	useEffect(() => {
		gl.setPixelRatio(window.devicePixelRatio)
		gl.setSize(size.width, size.height)
		gl.setClearColor(0xffaaff, 0)
	}, [gl, size.height, size.width])

	return null
}

export function World(props: WorldProps) {
	const { globeConfig } = props
	const scene = new Scene()
	scene.fog = new Fog(0xffffff, 400, 2000)
	return (
		<Canvas scene={scene} camera={new PerspectiveCamera(30, aspect, 180, 1800)}>
			<WebGLRendererConfig />
			<ambientLight color={globeConfig.ambientLight} intensity={0.6} />
			<directionalLight
				color={globeConfig.directionalLeftLight}
				position={new Vector3(-400, 100, 400)}
			/>
			<directionalLight
				color={globeConfig.directionalTopLight}
				position={new Vector3(-200, 500, 200)}
			/>
			<pointLight
				color={globeConfig.pointLight}
				position={new Vector3(-200, 500, 200)}
				intensity={0.8}
			/>
			<Globe {...props} />
			<CameraController
				startPosition={JAPAN_POSITION}
				endPosition={NETHERLANDS_POSITION}
				animationDuration={ANIMATION_DURATION}
			/>
		</Canvas>
	)
}

export function hexToRgb(hex: string) {
	var shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i
	hex = hex.replace(shorthandRegex, function (m, r, g, b) {
		return r + r + g + g + b + b
	})

	var result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
	return result
		? {
				r: parseInt(result[1]!, 16),
				g: parseInt(result[2]!, 16),
				b: parseInt(result[3]!, 16),
			}
		: null
}

export function genRandomNumbers(min: number, max: number, count: number) {
	const arr = []
	while (arr.length < count) {
		const r = Math.floor(Math.random() * (max - min)) + min
		if (arr.indexOf(r) === -1) arr.push(r)
	}

	return arr
}

function CameraController({
	startPosition,
	endPosition,
	animationDuration,
}: {
	startPosition: { lat: number; lng: number }
	endPosition: { lat: number; lng: number }
	animationDuration: number
}) {
	const controls = useRef<any>(null)

	useEffect(() => {
		if (controls.current) {
			// Set initial position
			controls.current.setAzimuthalAngle((-startPosition.lng * Math.PI) / 180)
			controls.current.setPolarAngle(((90 - startPosition.lat) * Math.PI) / 180)

			// Animate to Netherlands after a short delay
			setTimeout(() => {
				controls.current.autoRotateSpeed = 0 // Temporarily disable auto-rotation

				const startLng = (-startPosition.lng * Math.PI) / 180
				const endLng = (-endPosition.lng * Math.PI) / 180
				const startLat = ((90 - startPosition.lat) * Math.PI) / 180
				const endLat = ((90 - endPosition.lat) * Math.PI) / 180

				const startTime = Date.now()

				function animate() {
					const elapsed = Date.now() - startTime
					const progress = Math.min(elapsed / animationDuration, 1)

					// Use easeInOutCubic for smooth animation
					const eased =
						progress < 0.5
							? 4 * progress * progress * progress
							: 1 - Math.pow(-2 * progress + 2, 3) / 2

					const currentLng = startLng + (endLng - startLng) * eased
					const currentLat = startLat + (endLat - startLat) * eased

					controls.current.setAzimuthalAngle(currentLng)
					controls.current.setPolarAngle(currentLat)

					if (progress < 1) {
						requestAnimationFrame(animate)
					} else {
						controls.current.autoRotateSpeed = 1 // Re-enable auto-rotation
					}
				}

				animate()
			}, 1000) // Start animation after 1 second
		}
	}, [startPosition, endPosition, animationDuration])

	return (
		<OrbitControls
			ref={controls}
			enablePan={false}
			enableZoom={false}
			minDistance={cameraZ}
			maxDistance={cameraZ}
			autoRotateSpeed={1}
			autoRotate={true}
			minPolarAngle={Math.PI / 3.5}
			maxPolarAngle={Math.PI - Math.PI / 3}
		/>
	)
}
